import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { UseRadioGroupTransparent } from '../../lib/formik/Radio';
import {
  APPLICATION_TYPE,
  RiskProfileEnabled,
  SAVE_LATER_VALIDATION_CHECK,
} from '../../utils/constant';
import { MFTextField } from '../../lib/formik';
import { ProceedSaveLater } from './components';
import { useDispatch, useSelector } from 'react-redux';
import { QibQue } from '../../redux-store/actions';
import { RootStateType } from '../../redux-store/reducers';
import {
  applicationComparison,
  checkValidationBasedOnDate,
  getStep,
  saveForLater,
  statusRoleBased,
} from '../../utils/utilityFunctions';
import { updateApplication } from '../../redux-store/actions/application';
import { useHistory } from 'react-router';
import { useSnackbar } from 'notistack';
import { QibDetailsType, beneficialOwner, relatedParty } from '../../redux-store/types/api-types';
import {
  Box,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
  Grid,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { _buildQibValidationSchema } from '../../utils/schema';
import { useFeatureToggles } from '../../utils/useDataMdms';

export type Values = QibDetailsType;
export type _captureIfType = {
  id: string;
  value: string;
  [key: string]: string;
};

export type _singleQibQuestionType = {
  id: string;
  label: string;
  type: 'yesno' | 'multi' | 'text';
  options?: string[];
  remarks?: boolean;
  captureIf?: _captureIfType[];
  [key: string]: string | _captureIfType[] | boolean | string[] | undefined;
};

export type QibQuestionType = {
  section: string;
  remarkLabel?: string;
  applicationType?: 'individual' | 'non_individual';
  questions: _singleQibQuestionType[];
  [key: string]: string | _singleQibQuestionType[] | undefined;
};

export type QibValues = {
  qib?: Values;
  qibQue: QibQuestionType[];
  saveType: 'save for later' | 'save and proceed';
  beneficialowners?: beneficialOwner[];
  relatedparties?: relatedParty[];
  [key: string]:
    | Values
    | QibQuestionType[]
    | string
    | beneficialOwner[]
    | relatedParty[]
    | undefined;
};

export const emptyBeneficialOwner: beneficialOwner = {
  name: '',
  taxPayerIdType: '',
  taxPayerIdNumber: '',
  isActive: true,
};

export const emptyRelatedParty: relatedParty = {
  name: '',
  taxPayerIdType: '',
  taxPayerIdNumber: '',
  isActive: true,
};

export const MAX_BENEFICIAL_OWNERS = 3;
export const MAX_RELATED_PARTIES = 3;

export const DUE_DILIGENCE_VALIDATION_DATE = '2026-09-10';

export const shouldShowQuestion = (q: _singleQibQuestionType, values: QibValues): boolean => {
  if (!q.captureIf?.length) return true;

  return q.captureIf.every((c: _captureIfType) => values?.qib?.[c?.id] === c.value);
};

export const shouldShowRemarks = (q: _singleQibQuestionType, values: QibValues): boolean => {
  if (!q.remarks) return false;

  const val = values.qib?.[q.id];

  if (q.type === 'yesno') return val === 'yes';
  if (q.type === 'multi') return !!val && val !== 'None of above';

  return false;
};

export type _Props = {
  values: QibValues;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const QibForm: React.FC<_Props> = ({ values, handleChange }) => {
  const showQ = (q: _singleQibQuestionType) => shouldShowQuestion(q, values);

  const showR = (q: _singleQibQuestionType) => shouldShowRemarks(q, values);

  return (
    <Box>
      {values.qibQue.map((section: QibQuestionType, i: number) => (
        <Box key={i} sx={{ mb: 4 }}>
          {/* Section Title */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            {section.section}
          </Typography>

          <Divider aria-hidden="true" sx={{ mb: 3 }} />

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col" sx={{ fontWeight: 'bold', width: '35%' }}>
                    Question
                  </TableCell>

                  <TableCell component="th" scope="col" sx={{ fontWeight: 'bold', width: '35%' }}>
                    Response
                  </TableCell>

                  <TableCell component="th" scope="col" sx={{ fontWeight: 'bold', width: '30%' }}>
                    {section.remarkLabel ?? 'Remarks'}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {section.questions.map((q) => {
                  if (!showQ(q)) return null;

                  return (
                    <TableRow key={q.id}>
                      {/* Question Label */}
                      <TableCell component="th" scope="row" sx={{ verticalAlign: 'top' }}>
                        {q.label}
                      </TableCell>

                      {/* Response */}
                      <TableCell>
                        {/* YES/NO */}
                        {q.type === 'yesno' && (
                          <UseRadioGroupTransparent
                            formLabel=""
                            name={`qib.${q.id}`}
                            value={values.qib?.[q.id] ?? ''}
                            items={[
                              { label: 'Yes', value: 'yes' },
                              { label: 'No', value: 'no' },
                            ]}
                          />
                        )}

                        {/* MULTI */}
                        {q.type === 'multi' && q.options && (
                          <UseRadioGroupTransparent
                            formLabel=""
                            name={`qib.${q.id}`}
                            value={values.qib?.[q.id] ?? ''}
                            items={q.options.map((o) => ({
                              label: o,
                              value: o,
                            }))}
                          />
                        )}

                        {/* TEXT */}
                        {q.type === 'text' && (
                          <MFTextField
                            name={`qib.${q.id}`}
                            value={values.qib?.[q.id] ?? ''}
                            onChange={handleChange}
                            placeholder={`Enter ${q.label}`}
                          />
                        )}
                      </TableCell>

                      {/* Remarks */}
                      <TableCell>
                        {showR(q) && (
                          <MFTextField
                            name={`qib.${q.id}Remarks`}
                            value={values.qib?.[`${q.id}Remarks`] ?? ''}
                            onChange={handleChange}
                            placeholder="Enter remarks"
                            multiline
                            minRows={2}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

type _BeneficialOwnerProps = {
  values: QibValues;
  setValues: (values: QibValues) => void;
  disabled?: boolean;
  enabled?: boolean;
};

const BeneficialOwnerSection: React.FC<_BeneficialOwnerProps> = ({
  values,
  setValues,
  disabled = false,
  enabled = true,
}) => {
  const active = enabled && values.qib?.isBeneficialOwner === 'yes';

  const beneficialOwners: beneficialOwner[] = values.beneficialowners || [];
  useEffect(() => {
    if (active && beneficialOwners.filter((bo) => bo.isActive !== false).length === 0) {
      setValues({
        ...values,
        beneficialowners: [...beneficialOwners, { ...emptyBeneficialOwner }],
      });
    }
  }, [active]);

  if (!active) return null;

  const activeCount = beneficialOwners.filter((bo) => bo.isActive !== false).length;

  const addOwner = () => {
    if (activeCount >= MAX_BENEFICIAL_OWNERS) return;
    setValues({
      ...values,
      beneficialowners: [...beneficialOwners, { ...emptyBeneficialOwner }],
    });
  };

  const removeOwner = (index: number) => {
    const target = beneficialOwners[index];
    let next: beneficialOwner[];
    if (target?.id) {
      next = beneficialOwners.map((bo, i) => (i === index ? { ...bo, isActive: false } : bo));
    } else {
      next = beneficialOwners.filter((_, i) => i !== index);
    }
    if (!next.filter((bo) => bo.isActive !== false).length) {
      next = [...next, { ...emptyBeneficialOwner }];
    }
    setValues({ ...values, beneficialowners: next });
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Beneficial Owner Details
      </Typography>
      <Divider aria-hidden="true" sx={{ mb: 3 }} />

      {beneficialOwners.map((owner, index) =>
        owner.isActive === false ? null : (
          <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
            <Grid item xs={12} sm={4}>
              <MFTextField
                name={`beneficialowners.${index}.name`}
                label="Beneficiary Name *"
                placeholder="Enter Beneficiary Name"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MFTextField
                name={`beneficialowners.${index}.taxPayerIdType`}
                label="Tax Payer Identification Type *"
                placeholder="Enter Tax Payer Identification Type"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <MFTextField
                name={`beneficialowners.${index}.taxPayerIdNumber`}
                label="Tax Payer Identification Number *"
                placeholder="Enter Tax Payer Identification Number"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <Tooltip title="Remove">
                <span>
                  <IconButton
                    aria-label="Remove beneficial owner"
                    onClick={() => removeOwner(index)}
                    disabled={disabled}>
                    <DeleteOutlineIcon color="error" fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        )
      )}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addOwner}
        disabled={disabled || activeCount >= MAX_BENEFICIAL_OWNERS}
        sx={{
          mt: 1,
          fontWeight: 500,
          fontSize: 13,
          color: 'black',
          borderColor: 'black',
          '& .MuiButton-startIcon': { color: 'black' },
        }}>
        Add Beneficiary
      </Button>
      {activeCount >= MAX_BENEFICIAL_OWNERS && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          You can add a maximum of {MAX_BENEFICIAL_OWNERS} beneficiaries.
        </Typography>
      )}
    </Box>
  );
};

type _RelatedPartyProps = {
  values: QibValues;
  setValues: (values: QibValues) => void;
  disabled?: boolean;
  enabled?: boolean;
};

const RelatedPartySection: React.FC<_RelatedPartyProps> = ({
  values,
  setValues,
  disabled = false,
  enabled = true,
}) => {
  const active = enabled && values.qib?.isPartOfSameGroup === 'yes';

  const relatedParties: relatedParty[] = values.relatedparties || [];
  useEffect(() => {
    if (active && relatedParties.filter((rp) => rp.isActive !== false).length === 0) {
      setValues({ ...values, relatedparties: [...relatedParties, { ...emptyRelatedParty }] });
    }
  }, [active]);

  if (!active) return null;

  const activeCount = relatedParties.filter((rp) => rp.isActive !== false).length;

  const addParty = () => {
    if (activeCount >= MAX_RELATED_PARTIES) return;
    setValues({
      ...values,
      relatedparties: [...relatedParties, { ...emptyRelatedParty }],
    });
  };

  const removeParty = (index: number) => {
    const target = relatedParties[index];
    let next: relatedParty[];
    if (target?.id) {
      next = relatedParties.map((rp, i) => (i === index ? { ...rp, isActive: false } : rp));
    } else {
      next = relatedParties.filter((_, i) => i !== index);
    }
    if (!next.filter((rp) => rp.isActive !== false).length) {
      next = [...next, { ...emptyRelatedParty }];
    }
    setValues({ ...values, relatedparties: next });
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Related Party Details
      </Typography>
      <Divider aria-hidden="true" sx={{ mb: 3 }} />

      {relatedParties.map((party, index) =>
        party.isActive === false ? null : (
          <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
            <Grid item xs={12} sm={4}>
              <MFTextField
                name={`relatedparties.${index}.name`}
                label="Related Party Name *"
                placeholder="Enter Related Party Name"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MFTextField
                name={`relatedparties.${index}.taxPayerIdType`}
                label="Tax Payer Identification Type *"
                placeholder="Enter Tax Payer Identification Type"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <MFTextField
                name={`relatedparties.${index}.taxPayerIdNumber`}
                label="Tax Payer Identification Number *"
                placeholder="Enter Tax Payer Identification Number"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <Tooltip title="Remove">
                <span>
                  <IconButton
                    aria-label="Remove related party"
                    onClick={() => removeParty(index)}
                    disabled={disabled}>
                    <DeleteOutlineIcon color="error" fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        )
      )}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addParty}
        disabled={disabled || activeCount >= MAX_RELATED_PARTIES}
        sx={{
          mt: 1,
          fontWeight: 500,
          fontSize: 13,
          color: 'black',
          borderColor: 'black',
          '& .MuiButton-startIcon': { color: 'black' },
        }}>
        Add Related Party
      </Button>
      {activeCount >= MAX_RELATED_PARTIES && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          You can add a maximum of {MAX_RELATED_PARTIES} related parties.
        </Typography>
      )}
    </Box>
  );
};

export const _initialQibValues = {
  id: null,
  applicationId: null,

  isCitizenOfResidingCountry: '',
  isCitizenOfResidingCountryRemarks: '',
  isPartOfSameGroup: '',
  isPartOfSameGroupRemarks: '',
  isQualifiedBuyer: '',
  isQualifiedBuyerRemarks: '',
  reRegistrationNumber: '',
  isBeneficialOwner: '',
  isBeneficialOwnerRemarks: '',
  isControlledByAny: '',
  isControlledByAnyRemarks: '',
  isOutsideFundSetUp: '',
  isOutsideFundSetUpRemarks: '',
};

export default function QibDetailsScreen(): JSX.Element {
  const [loading, setLoading] = React.useState(false);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { toggler } = useFeatureToggles();
  const [initialQibValues, setInitialQibValues] = React.useState<QibValues>({
    qib: _initialQibValues,
    qibQue: [] as unknown as QibQuestionType[],
    saveType: 'save for later',
    beneficialowners: [],
    relatedparties: [],
  });
  const dispatch = useDispatch();
  const { application } = useSelector((store: RootStateType) => store.application);
  const {
    auth: { role },
  } = useSelector((store: RootStateType) => store);
  const [qibJson, setQibJson] = React.useState<QibQuestionType[]>([]);
  useEffect(() => {
    // Fetch existing data from API and set as initial values
    async function fetchQibData() {
      setLoading(true);
      try {
        // Simulate API call
        const res = ((await dispatch(QibQue())) || []) as unknown as QibQuestionType[];
        setQibJson(res);
      } catch (error) {
        console.error('Error fetching QIB data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchQibData();
  }, []);
  useEffect(() => {
    const hasSavedQibDetail = Object.keys(application?.qibDetail || {}).length > 0;
    const existingBeneficialOwners = application?.beneficialowners || [];
    const existingRelatedParties = application?.relatedparties || [];
    setInitialQibValues((prev) => ({
      ...prev,
      qib: hasSavedQibDetail
        ? ({
            ...prev.qib,
            ...application?.qibDetail,
          } as QibDetailsType)
        : prev.qib,
      qibQue: qibJson.filter(
        (section) =>
          section.applicationType === application?.applicationType || !section.applicationType
      ),
      beneficialowners: existingBeneficialOwners.length
        ? existingBeneficialOwners.map((bo) => ({ ...bo }))
        : [{ ...emptyBeneficialOwner }],
      relatedparties: existingRelatedParties.length
        ? existingRelatedParties.map((rp) => ({ ...rp }))
        : [{ ...emptyRelatedParty }],
    }));
  }, [application, qibJson]);

  function buildQibPayloadForBE(values: QibValues, qibJson: QibQuestionType[]) {
    // eslint-disable-next-line
    const payload: any = { qib: {} };

    qibJson.forEach((section) => {
      section.questions.forEach((q) => {
        const isVisible = shouldShowQuestion(q, values);
        const isRemarksVisible = shouldShowRemarks(q, values);

        // QUESTION
        if (isVisible) {
          const key = q.id;
          const val = values.qib?.[key];

          if (val !== undefined && val !== null && val !== '') {
            payload.qib[key] = val;
          }
        }

        // REMARKS
        if (isRemarksVisible) {
          const remarksKey = `${q.id}Remarks`;
          const remarksVal = values.qib?.[remarksKey];

          if (remarksVal !== undefined && remarksVal !== null && remarksVal !== '') {
            payload.qib[remarksKey] = remarksVal;
          }
        }
      });
    });

    return payload;
  }

  const handleSubmit = async (values: QibValues) => {
    try {
      const {
        id,
        applicant1ReferenceId = '',
        currentStep,
        applicationNumber,
        topupApplicationNumber,
      } = application || {};
      const { qib, saveType } = values;
      const isSaveLater = saveType !== 'save and proceed';

      const isBeneficialOwnerYes = qib?.isBeneficialOwner === 'yes';
      const isPartOfSameGroupYes = qib?.isPartOfSameGroup === 'yes';
      const enforceDueDiligenceDetails = checkValidationBasedOnDate(
        application?.createdAt || '',
        DUE_DILIGENCE_VALIDATION_DATE
      );
      const cleanedBeneficialOwners = (values.beneficialowners || []).filter((bo) => {
        if (bo.id) return true;
        return !!(bo.name?.trim() || bo.taxPayerIdType?.trim() || bo.taxPayerIdNumber?.trim());
      });
      const cleanedRelatedParties = (values.relatedparties || []).filter((rp) => {
        if (rp.id) return true;
        return !!(rp.name?.trim() || rp.taxPayerIdType?.trim() || rp.taxPayerIdNumber?.trim());
      });

      if (!isSaveLater && enforceDueDiligenceDetails && isBeneficialOwnerYes) {
        const activeOwners = cleanedBeneficialOwners.filter((bo) => bo.isActive !== false);
        if (activeOwners.length === 0) {
          throw 'Please add at least one Beneficial Owner';
        }
        if (activeOwners.length > MAX_BENEFICIAL_OWNERS) {
          throw `A maximum of ${MAX_BENEFICIAL_OWNERS} Beneficial Owners can be added`;
        }
        for (const owner of activeOwners) {
          if (!owner.name?.trim()) {
            throw 'Beneficiary Name is required for each beneficial owner';
          }
          if (!owner.taxPayerIdType?.trim()) {
            throw 'Tax Payer Identification Type is required for each beneficial owner';
          }
          if (!owner.taxPayerIdNumber?.trim()) {
            throw 'Tax Payer Identification Number is required for each beneficial owner';
          }
        }
      }

      if (!isSaveLater && enforceDueDiligenceDetails && isPartOfSameGroupYes) {
        const activeParties = cleanedRelatedParties.filter((rp) => rp.isActive !== false);
        if (activeParties.length === 0) {
          throw 'Please add at least one Related Party';
        }
        if (activeParties.length > MAX_RELATED_PARTIES) {
          throw `A maximum of ${MAX_RELATED_PARTIES} Related Parties can be added`;
        }
        for (const party of activeParties) {
          if (!party.name?.trim()) {
            throw 'Related Party Name is required for each related party';
          }
          if (!party.taxPayerIdType?.trim()) {
            throw 'Tax Payer Identification Type is required for each related party';
          }
          if (!party.taxPayerIdNumber?.trim()) {
            throw 'Tax Payer Identification Number is required for each related party';
          }
        }
      }

      const updatedPayload = {
        qibDetail: {
          ..._initialQibValues,
          ...(qib?.id ? { id: Number(qib?.id) } : {}),
          ...buildQibPayloadForBE(values, initialQibValues.qibQue).qib,
          applicationId: id ? Number(id) : undefined,
        },
        beneficialowners: isBeneficialOwnerYes
          ? cleanedBeneficialOwners
          : cleanedBeneficialOwners.filter((bo) => bo.id).map((bo) => ({ ...bo, isActive: false })),
        relatedparties: isPartOfSameGroupYes
          ? cleanedRelatedParties
          : cleanedRelatedParties.filter((rp) => rp.id).map((rp) => ({ ...rp, isActive: false })),
      };
      const checkApplication = applicationComparison(application, {
        ...application,
        ...updatedPayload,
        currentStep:
          !!currentStep &&
          currentStep >
            (application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
              ? RiskProfileEnabled
                ? getStep(10, isSaveLater)
                : getStep(9, isSaveLater)
              : getStep(6, isSaveLater)) -
              1
            ? currentStep
            : Number(currentStep) + 1,
      });
      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              ...updatedPayload,
              status: statusRoleBased(role, application),
              currentStep:
                application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                  ? RiskProfileEnabled
                    ? getStep(10, isSaveLater)
                    : getStep(9, isSaveLater)
                  : getStep(6, isSaveLater),
              ...((!SAVE_LATER_VALIDATION_CHECK as boolean) && { saveForLater: isSaveLater }),
              //!!currentStep && currentStep > 4 ? currentStep : Number(currentStep) + 1,
            },
            applicationId: id,
            ...(isSaveLater && { toastMessage: '' }),
          })
        );
        saveType === 'save and proceed'
          ? history.push(
              application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                ? 'fatca'
                : checkValidationBasedOnDate(
                    application?.createdAt || '',
                    toggler?.dateFeatureToggles?.nomineeRemovalAndDeclarationValidationDate || ''
                  )
                ? 'bank-details'
                : 'nominee-details',
              {
                id,
                applicant1ReferenceId,
                topUpApplication: application?.topupInitiated,
              }
            )
          : history.push(
              saveForLater(role, id, applicant1ReferenceId, application?.topupInitiated),
              {
                topUpApplication: application?.topupInitiated,
              }
            );
      } else if (checkApplication) {
        if (isSaveLater) {
          enqueueSnackbar(
            `Application ${
              application?.topupInitiated ? topupApplicationNumber : applicationNumber
            } - ` + ' Saved successfully',
            {
              variant: 'success',
              autoHideDuration: 3000,
            }
          );
        }
        !isSaveLater
          ? history.push(
              application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                ? 'fatca'
                : checkValidationBasedOnDate(
                    application?.createdAt || '',
                    toggler?.dateFeatureToggles?.nomineeRemovalAndDeclarationValidationDate || ''
                  )
                ? 'bank-details'
                : 'nominee-details',
              {
                id,
                applicant1ReferenceId,
                topupApplication: application?.topupInitiated,
              }
            )
          : history.push(
              saveForLater(role, id, applicant1ReferenceId, application?.topupInitiated),
              {
                topUpApplication: application?.topupInitiated,
              }
            );
      }
    } catch (e) {
      setLoading(false);
      if (typeof e === 'string') {
        enqueueSnackbar(e, { variant: 'error', autoHideDuration: 3000 });
      } else {
        console.error((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <Formik
      initialValues={initialQibValues}
      validationSchema={_buildQibValidationSchema(initialQibValues.qibQue)}
      enableReinitialize={true}
      onSubmit={handleSubmit}>
      {({ values, handleChange, setValues }) => (
        <Form>
          <QibForm values={values} handleChange={handleChange} />

          <BeneficialOwnerSection
            values={values}
            setValues={setValues}
            disabled={!!application?.topupInitiated}
            enabled={checkValidationBasedOnDate(
              application?.createdAt || '',
              DUE_DILIGENCE_VALIDATION_DATE
            )}
          />

          <RelatedPartySection
            values={values}
            setValues={setValues}
            disabled={!!application?.topupInitiated}
            enabled={checkValidationBasedOnDate(
              application?.createdAt || '',
              DUE_DILIGENCE_VALIDATION_DATE
            )}
          />

          <ProceedSaveLater
            saveLater={() => {
              setValues({
                ...values,
                saveType: 'save for later',
              });
            }}
            saveAndProceed={() => {
              setValues({
                ...values,
                saveType: 'save and proceed',
              });
            }}
            loader={loading}
            clickedButton={values.saveType}
          />
        </Form>
      )}
    </Formik>
  );
}
